/**
 * Embedded Stripe Payment Element modal for paying a single invoice.
 *
 * Flow:
 *   1. Parent passes an { invoice } prop and an accountId.
 *   2. On open we POST /accounts/:aid/pay/intent { invoice_id } to get
 *      { client_secret, payment_intent_id, transaction_id, amount }.
 *   3. We mount <Elements clientSecret> containing <PaymentElement />.
 *   4. On submit we call stripe.confirmPayment({ elements, redirect:'if_required' }).
 *   5. When paymentIntent.status === 'succeeded' locally we poll
 *      /pay/status/:pi until zoho_recorded=true, then show success and
 *      trigger the dashboard refresh via onSuccess().
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { CheckCircle2, Loader2, Lock, X } from 'lucide-react';

import { paymentApi } from '@/lib/api';
import { PAYMENT_MODAL } from '@/constants/testIds';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function fmtCurrency(v) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Number(v) || 0);
  } catch {
    return `$${(Number(v) || 0).toFixed(2)}`;
  }
}

function InnerPayForm({ intent, accountId, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('collect'); // collect | confirming | polling | done
  const [zohoRecorded, setZohoRecorded] = useState(false);
  const attemptedRef = useRef(false);
  const [formReady, setFormReady] = useState(false);

  // Funnel instrumentation — fire-and-forget, never affects the flow.
  const track = useCallback(
    (event, extra = {}) => {
      paymentApi
        .trackPayEvent(accountId, {
          event,
          invoice_id: intent.invoice_id,
          payment_intent_id: intent.payment_intent_id,
          ...extra,
        })
        .catch(() => {});
    },
    [accountId, intent.invoice_id, intent.payment_intent_id],
  );

  // Form became interactive (PaymentElement onReady); "abandoned" only if
  // a READY form is closed without any submit — phantom mounts during
  // Stripe's async init never fire events.
  useEffect(() => {
    if (!formReady) return undefined;
    track('form_loaded');
    return () => {
      if (!attemptedRef.current) track('abandoned');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formReady]);

  // Liveness guard for async loops (unmount = stop everything).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const pollZoho = useCallback(async (paymentIntentId) => {
    // Poll for up to ~30 s for the Zoho record to complete. Aborts the
    // moment the modal unmounts so closing the window never leaves a
    // loop firing API calls in the background.
    for (let i = 0; i < 15; i += 1) {
      if (!aliveRef.current) return;
      try {
        const { data } = await paymentApi.payStatus(accountId, paymentIntentId);
        if (!aliveRef.current) return;
        if (data.zoho_recorded) {
          setZohoRecorded(true);
          return;
        }
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
    }
  }, [accountId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError('');
    setPhase('confirming');
    attemptedRef.current = true;
    track('submit_attempted');
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (err) {
      track('failure', {
        error_code: err.code || err.type || 'stripe_error',
        error_message: err.message || '',
      });
      setError(err.message || 'Payment failed. Please try again.');
      setSubmitting(false);
      setPhase('collect');
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      track('success');
      setPhase('polling');
      await pollZoho(paymentIntent.id);
      setPhase('done');
    } else {
      track('failure', {
        error_code: 'unexpected_pi_status',
        error_message: paymentIntent?.status || 'unknown',
      });
      setError(`Payment is ${paymentIntent?.status || 'pending'}. Please refresh in a moment.`);
      setPhase('collect');
    }
    setSubmitting(false);
  };

  if (phase === 'done') {
    return (
      <div
        className="flex flex-col items-center text-center px-2 py-6"
        data-testid={PAYMENT_MODAL.successState}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#DDF3E4] mb-4">
          <CheckCircle2 className="h-7 w-7 text-[#0F5132]" />
        </span>
        <h3 className="font-heading uppercase text-xl text-[#0B1930]">
          Payment received
        </h3>
        <p className="mt-2 text-sm text-[#4B5563]">
          {fmtCurrency(intent.amount)} paid on invoice{' '}
          <span className="font-semibold">{intent.invoice_number}</span>.
        </p>
        <p className="mt-1 text-xs text-[#4B5563]">
          {zohoRecorded
            ? 'Zoho Books has been updated.'
            : 'Zoho Books will update within a minute.'}
        </p>
        <button
          type="button"
          onClick={() => {
            onSuccess?.();
            onClose();
          }}
          className="mt-6 inline-flex items-center justify-center rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors"
          data-testid={PAYMENT_MODAL.successDoneBtn}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="rounded-xl bg-[#F5F1EA] border border-[#EDE5D4] px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#4B5563]">
            Invoice
          </p>
          <p
            className="text-sm font-semibold text-[#0B1930]"
            data-testid={PAYMENT_MODAL.invoiceNumber}
          >
            {intent.invoice_number}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wide text-[#4B5563]">
            Amount
          </p>
          <p
            className="font-heading text-xl text-[#0B1930]"
            data-testid={PAYMENT_MODAL.amount}
          >
            {fmtCurrency(intent.amount)}
          </p>
        </div>
      </div>

      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => setFormReady(true)}
      />

      {error && (
        <p
          className="text-sm text-red-600"
          data-testid={PAYMENT_MODAL.errorMsg}
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-4 py-3 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        data-testid={PAYMENT_MODAL.submitBtn}
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {phase === 'polling' ? 'Recording…' : 'Processing…'}
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" />
            Pay {fmtCurrency(intent.amount)}
          </>
        )}
      </button>

      <p className="text-[11px] text-center text-[#4B5563] leading-relaxed">
        Payments are processed securely by Stripe. We never store your card details.
      </p>
    </form>
  );
}

export default function PayInvoiceModal({ open, onClose, accountId, invoice, onSuccess }) {
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !invoice) return undefined;
    let cancelled = false;
    setIntent(null);
    setError('');
    setLoading(true);
    (async () => {
      try {
        const { data } = await paymentApi.createPayIntent(accountId, invoice.invoice_id);
        if (cancelled) return;
        setIntent(data);
        paymentApi
          .trackPayEvent(accountId, {
            event: 'initiation',
            invoice_id: invoice.invoice_id,
            payment_intent_id: data.payment_intent_id,
          })
          .catch(() => {});
      } catch (err) {
        if (cancelled) return;
        paymentApi
          .trackPayEvent(accountId, {
            event: 'failure',
            invoice_id: invoice.invoice_id,
            error_code: 'intent_create_failed',
            error_message: err?.detail || 'createPayIntent failed',
          })
          .catch(() => {});
        setError(err?.detail || 'Unable to start payment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, invoice, accountId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
      data-testid={PAYMENT_MODAL.modal}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#EDE5D4] px-6 py-4 shrink-0">
          <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-lg">
            Pay invoice
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#F5F1EA] transition-colors"
            aria-label="Close"
            data-testid={PAYMENT_MODAL.closeBtn}
          >
            <X className="h-5 w-5 text-[#4B5563]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div
              className="flex items-center justify-center py-10 text-[#4B5563] gap-2"
              data-testid={PAYMENT_MODAL.processing}
            >
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Preparing secure payment…</span>
            </div>
          )}
          {error && !loading && (
            <p
              className="text-sm text-red-600 text-center py-6"
              data-testid={PAYMENT_MODAL.errorMsg}
            >
              {error}
            </p>
          )}
          {intent && !loading && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: intent.client_secret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#C8A46B',
                    colorText: '#0B1930',
                    fontFamily: 'system-ui, sans-serif',
                    borderRadius: '10px',
                  },
                },
              }}
            >
              <InnerPayForm
                intent={intent}
                accountId={accountId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            </Elements>
          )}
          {!stripePromise && !loading && (
            <p className="text-sm text-red-600 text-center py-6">
              Payments are not configured. Please contact ZMEX support.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
